import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Pressable, View, ViewStyle } from 'react-native'

import OpenIcon from '@common/assets/svg/OpenIcon'
import Alert from '@common/components/Alert'
import Button from '@common/components/Button'
import Spinner from '@common/components/Spinner'
import Text from '@common/components/Text'
import TokenIcon from '@common/components/TokenIcon'
import useTheme from '@common/hooks/useTheme'
import { RADII } from '@common/modules/dashboard/home-kit/tokens'
import flexbox from '@common/styles/utils/flexbox'
import type { DcaFlowStep } from '@web/extension-services/background/controllers/dca/dca'
import {
  DcaExecution,
  DcaOrderDetail,
  DcaToken
} from '@web/extension-services/background/controllers/dca/types'
import { openInTab } from '@web/extension-services/background/webapi/tab'
import { DCA_CHAIN_ID } from '@web/modules/swap-and-bridge/hooks/useDca'

import DcaFlowStatus, { DcaFlowPhase } from '../DcaFlowStatus'
import DcaModalShell from '../DcaModalShell'
import DcaRow from '../DcaRow'
import { getDcaManageErrorText } from '../errorText'
import { formatDcaAmount, formatDcaDateTimeShort, formatDcaInterval } from '../format'
import { getExecutionReasonText, getExecutionStatusText, getPauseReasonText } from './historyText'
import {
  formatRate,
  getProgressPercent,
  getRate,
  getReasonCode,
  getToken,
  getTotalBudgetRaw,
  getUpcomingExecutionAt,
  isCancellable,
  isOpenOrder
} from './orderView'
import { DcaPair, DcaProgressBar, DcaStatusPill, DcaTabs, tokenLabel } from './parts'
import { UseDcaHistoryReturn } from './useDcaHistory'

type Props = {
  sheetRef: React.RefObject<any>
  history: UseDcaHistoryReturn
  onClose: () => void
  autoOpen?: boolean
  /** Out of the way while a cancel step is signed on mobile-web (see DcaModalShell). */
  hidden?: boolean
}

type DetailTab = 'overview' | 'executions'

const txUrl = (explorerUrl: string | null, txHash: string) =>
  explorerUrl ? `${explorerUrl.replace(/\/$/, '')}/tx/${txHash}` : null

const ViewTxLink = ({ url, label }: { url: string | null; label: string }) => {
  const { theme } = useTheme()
  if (!url) return null
  return (
    <Pressable
      onPress={() => openInTab({ url })}
      style={[flexbox.directionRow, flexbox.alignCenter, { gap: 4 }]}
    >
      <Text fontSize={13} weight="semiBold" color={theme.linkText as string}>
        {label}
      </Text>
      <OpenIcon width={12} height={12} color={theme.linkText as string} />
    </Pressable>
  )
}

const amountOf = (raw: string | bigint | null | undefined, token: DcaToken | null) =>
  token ? formatDcaAmount(raw, token.decimals) : '-'

// ---------------------------------------------------------------- overview

const Overview = ({
  order,
  tokens,
  canCancel,
  onCancel
}: {
  order: DcaOrderDetail
  tokens: Record<string, DcaToken>
  canCancel: boolean
  onCancel: () => void
}) => {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const tokenIn = getToken(tokens, order.tokenIn)
  const tokenOut = getToken(tokens, order.tokenOut)
  const inSymbol = tokenLabel(tokenIn, order.tokenIn)
  const outSymbol = tokenLabel(tokenOut, order.tokenOut)
  const percent = getProgressPercent(order.executedCount, order.totalExecutions)
  const isOpen = isOpenOrder(order.status)
  const budget = getTotalBudgetRaw(order)
  const spent = BigInt(order.totals.amountInRaw || '0')
  const spentPercent = budget > 0n ? Number((spent * 100n) / budget) : 0
  const averageRate =
    tokenIn && tokenOut
      ? getRate({
          amountInRaw: order.totals.amountInRaw,
          feeRaw: order.totals.feeRaw,
          amountOutRaw: order.totals.amountOutRaw,
          decimalsIn: tokenIn.decimals,
          decimalsOut: tokenOut.decimals
        })
      : null

  const notice = (() => {
    if (order.status === 'PAUSED') return getPauseReasonText(order.pauseReason, t)
    if (order.status === 'EXPIRED')
      return t(
        'This DCA expired before all buys ran. Cancel it to remove the token approval it still holds.'
      )
    if (order.status === 'ACTIVE' && !order.supported)
      return t(
        'This pair is no longer supported, so no more buys will run. Cancel it to remove the token approval.'
      )
    return null
  })()

  return (
    <View style={{ gap: 16 }} testID="dca-order-overview">
      <View
        style={[
          flexbox.directionRow,
          flexbox.alignCenter,
          flexbox.justifySpaceBetween,
          { gap: 12 }
        ]}
      >
        <DcaPair
          tokenIn={tokenIn}
          tokenOut={tokenOut}
          tokenInAddress={order.tokenIn}
          tokenOutAddress={order.tokenOut}
        />
        <DcaStatusPill status={order.status} />
      </View>

      {!!notice && <Alert type="warning" size="sm" title={notice} />}

      <View style={{ gap: 4 }}>
        <Text fontSize={13} appearance="secondaryText">
          {t('Total allocation')}
        </Text>
        <Text fontSize={22} weight="semiBold" testID="dca-order-total">
          {`${amountOf(budget, tokenIn)} ${inSymbol}`}
        </Text>
      </View>

      <View style={{ gap: 8 }}>
        <DcaProgressBar percent={percent} />
        <View style={[flexbox.directionRow, flexbox.justifySpaceBetween, { gap: 12 }]}>
          <Text fontSize={13} weight="medium">
            {t('{{done}} of {{count}} buys completed', {
              done: order.executedCount,
              count: order.totalExecutions
            })}
          </Text>
          <Text fontSize={13} weight="medium">{`${percent}%`}</Text>
        </View>
      </View>

      <View
        style={{
          backgroundColor: theme.secondaryBackground as string,
          borderRadius: RADII.md,
          paddingHorizontal: 12,
          paddingVertical: 8
        }}
      >
        <DcaRow
          label={t('Left to spend')}
          value={`${amountOf(order.remainingAmountRaw, tokenIn)} ${inSymbol}`}
          testID="dca-order-remaining"
        />
        <DcaRow
          label={t('Received so far')}
          value={`${amountOf(order.totals.amountOutRaw, tokenOut)} ${outSymbol}`}
          testID="dca-order-received"
        />
        <DcaRow
          label={t('Average price')}
          value={
            averageRate === null ? '-' : `1 ${inSymbol} ≈ ${formatRate(averageRate)} ${outSymbol}`
          }
        />
        <Text fontSize={12} appearance="tertiaryText" style={{ marginTop: 4 }}>
          {t('DCA never locks your funds: what is left to spend stays in your wallet.')}
        </Text>
      </View>

      <View>
        <DcaRow
          divided
          label={t('Total spent')}
          value={
            <Text fontSize={14} weight="medium" style={{ textAlign: 'right', flexShrink: 1 }}>
              {`${amountOf(spent, tokenIn)} ${inSymbol} `}
              <Text fontSize={14} weight="medium" color={theme.successText as string}>
                {`(${spentPercent}%)`}
              </Text>
            </Text>
          }
        />
        <DcaRow
          divided
          label={t('Amount per buy')}
          value={`${amountOf(order.amountPerExecutionRaw, tokenIn)} ${inSymbol}`}
        />
        <DcaRow
          divided
          label={t('Buying')}
          value={
            <View style={[flexbox.directionRow, flexbox.alignCenter, { gap: 6 }]}>
              <TokenIcon
                width={18}
                height={18}
                withNetworkIcon={false}
                address={order.tokenOut}
                chainId={BigInt(DCA_CHAIN_ID)}
                uri={tokenOut?.logoUri || undefined}
              />
              <Text fontSize={14} weight="medium">
                {outSymbol}
              </Text>
            </View>
          }
        />
        <DcaRow
          divided
          label={t('Interval')}
          value={t('Every {{interval}}', { interval: formatDcaInterval(order.interval, t) })}
        />
        {/* The schedule rows only mean something while the order can still run. */}
        {isOpen && (
          <>
            <DcaRow divided label={t('Buys left')} value={String(order.remainingExecutions)} />
            <DcaRow
              divided
              label={t('Next buy')}
              value={
                getUpcomingExecutionAt(order.nextExecutionAt)
                  ? formatDcaDateTimeShort(order.nextExecutionAt)
                  : t('Due now')
              }
            />
          </>
        )}
        <DcaRow
          divided
          label={t('Max slippage')}
          value={`${(order.maxSlippageBps / 100).toFixed(2).replace(/\.?0+$/, '')}%`}
        />
        <DcaRow
          divided
          label={t('ZKX fee')}
          value={`${(order.feeBps / 100).toFixed(2).replace(/\.?0+$/, '')}%`}
        />
        {isOpen && (
          <DcaRow divided label={t('Ends by')} value={formatDcaDateTimeShort(order.deadline)} />
        )}
        <DcaRow label={t('Created')} value={formatDcaDateTimeShort(order.createdAt)} />
      </View>

      {canCancel && (
        <View
          style={{
            position: 'sticky' as ViewStyle['position'],
            bottom: 0,
            paddingTop: 8,
            backgroundColor: theme.primaryBackground as string
          }}
        >
          <Button
            text={t('Cancel DCA')}
            onPress={onCancel}
            hasBottomSpacing={false}
            testID="dca-order-cancel-btn"
          />
        </View>
      )}
    </View>
  )
}

// -------------------------------------------------------------- executions

const ExecutionCard = ({
  execution,
  order,
  tokens,
  explorerUrl
}: {
  execution: DcaExecution
  order: DcaOrderDetail
  tokens: Record<string, DcaToken>
  explorerUrl: string | null
}) => {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const tokenIn = getToken(tokens, order.tokenIn)
  const tokenOut = getToken(tokens, order.tokenOut)
  const inSymbol = tokenLabel(tokenIn, order.tokenIn)
  const outSymbol = tokenLabel(tokenOut, order.tokenOut)
  const isSuccess = execution.status === 'SUCCESS'
  const rate =
    isSuccess && tokenIn && tokenOut
      ? getRate({
          amountInRaw: execution.amountInRaw,
          feeRaw: execution.feeRaw,
          amountOutRaw: execution.amountOutRaw,
          decimalsIn: tokenIn.decimals,
          decimalsOut: tokenOut.decimals
        })
      : null

  return (
    <View
      style={{
        backgroundColor: theme.secondaryBackground as string,
        borderRadius: RADII.md,
        padding: 12,
        gap: 6
      }}
      testID={`dca-execution-${execution.status}`}
    >
      <View
        style={[
          flexbox.directionRow,
          flexbox.alignCenter,
          flexbox.justifySpaceBetween,
          { gap: 12 }
        ]}
      >
        <DcaPair
          tokenIn={tokenIn}
          tokenOut={tokenOut}
          tokenInAddress={order.tokenIn}
          tokenOutAddress={order.tokenOut}
          size={24}
        />
        {execution.txHash ? (
          <ViewTxLink url={txUrl(explorerUrl, execution.txHash)} label={t('View')} />
        ) : null}
      </View>
      {isSuccess ? (
        <>
          <DcaRow
            label={t('Spent')}
            value={`${amountOf(execution.amountInRaw, tokenIn)} ${inSymbol}`}
          />
          <DcaRow
            label={t('Received')}
            value={`${amountOf(execution.amountOutRaw, tokenOut)} ${outSymbol}`}
          />
          <DcaRow
            label={t('Rate')}
            value={rate === null ? '-' : `1 ${inSymbol} ≈ ${formatRate(rate)} ${outSymbol}`}
          />
        </>
      ) : (
        <DcaRow
          label={getExecutionStatusText(execution.status, t)}
          value={
            <Text
              fontSize={14}
              weight="medium"
              color={theme.warningText as string}
              style={{ textAlign: 'right', flexShrink: 1 }}
            >
              {getExecutionReasonText(getReasonCode(execution.reason), t)}
            </Text>
          }
        />
      )}
      <DcaRow label={t('Date/Time')} value={formatDcaDateTimeShort(execution.executedAt)} />
    </View>
  )
}

// ------------------------------------------------------------------ cancel

const CancelConfirm = ({
  order,
  tokens,
  onKeep,
  onConfirm
}: {
  order: DcaOrderDetail
  tokens: Record<string, DcaToken>
  onKeep: () => void
  onConfirm: () => void
}) => {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const tokenIn = getToken(tokens, order.tokenIn)
  const tokenOut = getToken(tokens, order.tokenOut)
  const inSymbol = tokenLabel(tokenIn, order.tokenIn)

  return (
    <View style={{ gap: 16 }} testID="dca-cancel-confirm">
      <View style={flexbox.alignCenter}>
        <DcaPair
          vertical
          tokenIn={tokenIn}
          tokenOut={tokenOut}
          tokenInAddress={order.tokenIn}
          tokenOutAddress={order.tokenOut}
          size={40}
          fontSize={20}
        />
      </View>
      <View>
        <DcaRow
          label={t('Buys completed')}
          value={`${order.executedCount}/${order.totalExecutions}`}
          emphasis
        />
        <DcaRow label={t('Remaining buys')} value={String(order.remainingExecutions)} emphasis />
        <DcaRow
          label={t('Already received')}
          value={`${amountOf(order.totals.amountOutRaw, tokenOut)} ${tokenLabel(tokenOut, order.tokenOut)}`}
          emphasis
        />
      </View>
      <View
        style={{
          backgroundColor: theme.secondaryBackground as string,
          borderRadius: RADII.md,
          padding: 16,
          gap: 6
        }}
      >
        <Text fontSize={13} appearance="secondaryText">
          {t('Not spent, stays in your wallet')}
        </Text>
        <Text fontSize={22} weight="semiBold" testID="dca-cancel-unspent">
          {`${amountOf(order.remainingAmountRaw, tokenIn)} ${inSymbol}`}
        </Text>
      </View>
      <View style={[flexbox.directionRow, { gap: 12 }]}>
        <Button
          type="secondary"
          text={t('Keep DCA')}
          onPress={onKeep}
          hasBottomSpacing={false}
          style={{ flex: 1 }}
          // Two buttons share a phone-width row: at 16px "Conserver le DCA" wraps.
          textStyle={{ fontSize: 14 }}
          testID="dca-cancel-keep-btn"
        />
        <Button
          text={t('Cancel DCA')}
          onPress={onConfirm}
          hasBottomSpacing={false}
          style={{ flex: 1 }}
          textStyle={{ fontSize: 14 }}
          testID="dca-cancel-confirm-btn"
        />
      </View>
      <Text fontSize={13} appearance="secondaryText" style={{ textAlign: 'center' }}>
        {t(
          'Cancelling stops all future buys and lowers the token approval this DCA was using. Your funds stay in your wallet.'
        )}
      </Text>
    </View>
  )
}

const CancelProgress = ({
  history,
  tokenIn,
  tokenInSymbol,
  onClose,
  onViewOrder
}: {
  history: UseDcaHistoryReturn
  tokenIn: DcaToken | null
  tokenInSymbol: string
  onClose: () => void
  onViewOrder: () => void
}) => {
  const { t } = useTranslation()
  const { cancelStatus, cancelSteps, cancelSummary, cancelError, explorerUrl } = history
  const lowersApproval = cancelSteps.some((s) => s.id.startsWith('approve'))
  const phase: DcaFlowPhase = cancelStatus === 'idle' ? 'preparing' : cancelStatus
  const isSigning = cancelSteps.some((s) => s.status === 'signing')

  const header = (() => {
    if (phase === 'success')
      return {
        title: t('DCA cancelled'),
        subtitle: lowersApproval
          ? t('Future buys have been stopped and the remaining approval has been removed.')
          : t('Future buys have been stopped.')
      }
    if (phase === 'error')
      return {
        title: t('DCA not cancelled'),
        subtitle: getDcaManageErrorText(cancelError ?? 'GENERIC', t)
      }
    if (phase === 'preparing')
      return {
        title: t('Checking the cancellation...'),
        subtitle: t('Verifying the transactions before you sign them.')
      }
    return {
      title: t('Cancelling your DCA...'),
      subtitle: isSigning
        ? t('Confirm the transaction in your wallet.')
        : t('Waiting for the transaction to confirm...')
    }
  })()

  const rows = cancelSummary
    ? [
        {
          label: t('Buys completed'),
          value: `${cancelSummary.executedCount}/${cancelSummary.totalExecutions}`
        },
        {
          label: t('Not spent, stays in your wallet'),
          value: `${amountOf(cancelSummary.remainingAmountRaw, tokenIn)} ${tokenInSymbol}`
        },
        { label: t('Network'), value: 'PulseChain' }
      ]
    : undefined

  const isDone = phase === 'success' || phase === 'error'

  return (
    <DcaFlowStatus
      phase={phase}
      steps={cancelSteps}
      getStepLabels={(step: DcaFlowStep) =>
        step.id === 'cancel-order'
          ? { label: t('Order cancelled'), activeLabel: t('Cancelling order') }
          : { label: t('Approval lowered'), activeLabel: t('Lowering approval') }
      }
      title={header.title}
      subtitle={header.subtitle}
      rows={rows}
      explorerUrl={explorerUrl}
      testID={`dca-cancel-${cancelStatus}`}
      secondary={
        isDone ? { text: t('Close'), onPress: onClose, testID: 'dca-cancel-close-btn' } : undefined
      }
      primary={
        isDone
          ? { text: t('View order'), onPress: onViewOrder, testID: 'dca-cancel-view-order-btn' }
          : undefined
      }
    />
  )
}

// ------------------------------------------------------------------- modal

/**
 * One sheet for an order: its detail (Overview / Executions), the cancel
 * confirmation and the cancel progress. Swapping content instead of stacking a
 * second sheet keeps it clear of the nested-modal issues on mobile-web.
 */
const DcaOrderModal = ({ sheetRef, history, onClose, autoOpen, hidden }: Props) => {
  const { t } = useTranslation()
  const [tab, setTab] = useState<DetailTab>('overview')
  const [screen, setScreen] = useState<'detail' | 'cancel'>('detail')
  // Hold the overview's height on the shorter Executions tab so the modal does
  // not jump (and re-centre) every time the tab changes.
  const [overviewHeight, setOverviewHeight] = useState(0)
  const { detail, detailStatus, detailOrderId, executions, tokens, cancelOrderId } = history

  // Every newly opened order starts on its overview.
  useEffect(() => {
    setTab('overview')
    setScreen('detail')
  }, [detailOrderId])

  const isCancelFlow =
    !!detailOrderId && cancelOrderId === detailOrderId && history.cancelStatus !== 'idle'

  const tokenIn = detail ? getToken(tokens, detail.tokenIn) : null
  const tokenOut = detail ? getToken(tokens, detail.tokenOut) : null
  const title = detail
    ? `${tokenLabel(tokenIn, detail.tokenIn)} → ${tokenLabel(tokenOut, detail.tokenOut)}`
    : t('DCA order')

  const body = (() => {
    if (isCancelFlow)
      return (
        <CancelProgress
          history={history}
          tokenIn={tokenIn}
          tokenInSymbol={detail ? tokenLabel(tokenIn, detail.tokenIn) : ''}
          onClose={onClose}
          onViewOrder={() => {
            setScreen('detail')
            setTab('overview')
            history.resetCancel()
          }}
        />
      )

    if (!detail) {
      if (detailStatus === 'indexing')
        return (
          <View
            style={[flexbox.alignCenter, { gap: 10, paddingVertical: 24 }]}
            testID="dca-order-indexing"
          >
            <Spinner style={{ width: 28, height: 28 }} />
            <Text fontSize={16} weight="semiBold" style={{ textAlign: 'center' }}>
              {t('Confirming your order...')}
            </Text>
            <Text fontSize={14} appearance="secondaryText" style={{ textAlign: 'center' }}>
              {t('It shows up here after 12 confirmations, about 2 minutes.')}
            </Text>
          </View>
        )
      if (detailStatus === 'error')
        return (
          <View style={{ gap: 16, paddingVertical: 12 }} testID="dca-order-error">
            <Text fontSize={14} appearance="secondaryText" style={{ textAlign: 'center' }}>
              {getDcaManageErrorText(history.detailError ?? 'GENERIC', t)}
            </Text>
            <Button
              type="secondary"
              text={t('Try again')}
              onPress={() => detailOrderId && history.openDetail(detailOrderId)}
              hasBottomSpacing={false}
            />
          </View>
        )
      return (
        <View style={[flexbox.center, { paddingVertical: 40 }]}>
          <Spinner style={{ width: 28, height: 28 }} />
        </View>
      )
    }

    if (screen === 'cancel')
      return (
        <CancelConfirm
          order={detail}
          tokens={tokens}
          onKeep={() => setScreen('detail')}
          onConfirm={() => history.cancelOrder(detail.orderId)}
        />
      )

    return (
      <View style={{ gap: 16 }}>
        <DcaTabs<DetailTab>
          testIDPrefix="dca-order-tab"
          value={tab}
          onChange={setTab}
          tabs={[
            { value: 'overview', label: t('Overview') },
            {
              value: 'executions',
              label: t('Executions ({{total}})', { total: executions.length })
            }
          ]}
        />
        {tab === 'overview' ? (
          <View onLayout={(e) => setOverviewHeight(e.nativeEvent.layout.height)}>
            <Overview
              order={detail}
              tokens={tokens}
              canCancel={history.canManage && isCancellable(detail.status)}
              onCancel={() => setScreen('cancel')}
            />
          </View>
        ) : executions.length ? (
          <View style={{ gap: 8, minHeight: overviewHeight }} testID="dca-order-executions">
            {executions.map((execution, i) => (
              <ExecutionCard
                // SKIPPED rows have no index or hash; their time is unique per slot.
                key={`${execution.executedAt}-${execution.txHash ?? i}`}
                execution={execution}
                order={detail}
                tokens={tokens}
                explorerUrl={history.explorerUrl}
              />
            ))}
          </View>
        ) : (
          <Text
            fontSize={14}
            appearance="secondaryText"
            style={{ textAlign: 'center', paddingVertical: 24, minHeight: overviewHeight }}
          >
            {t('No buys yet. The first one runs at the scheduled time.')}
          </Text>
        )}
      </View>
    )
  })()

  return (
    <DcaModalShell
      id="dca-order-modal"
      sheetRef={sheetRef}
      title={screen === 'cancel' && !isCancelFlow ? t('Cancel this DCA?') : title}
      variant={isCancelFlow ? 'status' : 'default'}
      onClose={onClose}
      // Backdrop / Esc close: closeDetail leaves a cancel in flight running.
      onClosed={onClose}
      autoOpen={autoOpen}
      hidden={hidden}
    >
      {body}
    </DcaModalShell>
  )
}

export default React.memo(DcaOrderModal)
